/* AP | DR — shared custom elements for the Direct Response sections.
   Pattern follows ap-product-showcase.js: IIFE, double-registration guard,
   theme.Slideshow (Flickity wrapper) from theme.js, design-mode listeners
   cleaned up in disconnectedCallback. */

(function() {
  'use strict';

  /* ---- <ap-dr-media> — auto-gallery ----
     Children are anonymous .shopify-block wrappers around image blocks.
     One image child renders as a plain image; two or more initialize a
     Flickity carousel (the wrappers become the cells). */
  if (!customElements.get('ap-dr-media')) {
    class ApDrMedia extends HTMLElement {
      connectedCallback() {
        if (document.readyState === 'loading') {
          this._onReady = this._init.bind(this);
          document.addEventListener('DOMContentLoaded', this._onReady);
        } else {
          this._init();
        }
      }

      _init() {
        var slides = Array.prototype.filter.call(this.children, function(child) {
          return child.querySelector('img, svg, image-element');
        });

        this.removeAttribute('data-gallery-pending');

        if (slides.length > 1 && window.theme && theme.Slideshow) {
          this.classList.add('ap-dr-media--carousel');
          this.slideshow = new theme.Slideshow(this, {
            prevNextButtons: this.dataset.arrows === 'true',
            pageDots: this.dataset.dots !== 'false',
            wrapAround: true,
            adaptiveHeight: true,
            cellAlign: 'center',
            /* slim chevron instead of Flickity's default arrow */
            arrowShape: 'M 62,12 L 25,50 L 62,88 L 70,80 L 41,50 L 70,20 Z'
          });

          /* Lazy images (ratio "auto") only know their height once loaded —
             re-measure so adaptiveHeight doesn't keep the collapsed value */
          this._onImageLoad = this._handleImageLoad.bind(this);
          this.addEventListener('load', this._onImageLoad, true);
        }

        if (window.Shopify && Shopify.designMode) {
          this._onBlockSelect = this._handleBlockSelect.bind(this);
          document.addEventListener('shopify:block:select', this._onBlockSelect);
        }
      }

      _handleBlockSelect(evt) {
        if (!this.slideshow || !this.contains(evt.target)) return;
        var cell = evt.target;
        while (cell && cell.parentElement !== this) {
          cell = cell.parentElement;
        }
        if (!cell) return;
        var index = Array.prototype.indexOf.call(this.children, cell);
        if (index > -1 && this.slideshow.slideshow) {
          this.slideshow.slideshow.select(index);
        }
      }

      _handleImageLoad(evt) {
        if (evt.target.tagName !== 'IMG') return;
        var flickity = this.slideshow && this.slideshow.slideshow;
        if (!flickity || this._resizeQueued) return;
        this._resizeQueued = true;
        requestAnimationFrame(function() {
          this._resizeQueued = false;
          flickity.resize();
        }.bind(this));
      }

      disconnectedCallback() {
        if (this._onReady) {
          document.removeEventListener('DOMContentLoaded', this._onReady);
        }
        if (this._onBlockSelect) {
          document.removeEventListener('shopify:block:select', this._onBlockSelect);
        }
        if (this._onImageLoad) {
          this.removeEventListener('load', this._onImageLoad, true);
        }
        if (this.slideshow && typeof this.slideshow.destroy === 'function') {
          this.slideshow.destroy();
        }
      }
    }

    customElements.define('ap-dr-media', ApDrMedia);
  }

  /* ---- <ap-dr-carousel> — generic peek carousel ----
     Cells are the anonymous .shopify-block wrappers (or plain children).
     Width comes from CSS: calc(100% / var(--per-row)) set by the section.
     data-per-row / data-per-row-mobile decide when Flickity is needed. */
  if (!customElements.get('ap-dr-carousel')) {
    class ApDrCarousel extends HTMLElement {
      connectedCallback() {
        if (document.readyState === 'loading') {
          this._onReady = this._init.bind(this);
          document.addEventListener('DOMContentLoaded', this._onReady);
        } else {
          this._init();
        }
      }

      _perRow() {
        var mobile = window.theme && theme.config && theme.config.bpSmall;
        var value = mobile ? this.dataset.perRowMobile : this.dataset.perRow;
        return parseFloat(value) || 3;
      }

      _init() {
        this._extractLeaders();
        this._build();

        this._onBreakpoint = this._rebuild.bind(this);
        document.addEventListener('matchSmall', this._onBreakpoint);
        document.addEventListener('unmatchSmall', this._onBreakpoint);

        if (window.Shopify && Shopify.designMode) {
          this._onBlockSelect = this._handleBlockSelect.bind(this);
          document.addEventListener('shopify:block:select', this._onBlockSelect);
        }
      }

      /* With data-cell-selector, only matching children are carousel
         cells; other blocks (headings, summary rating, text) move above
         the carousel so they don't become slides. */
      _extractLeaders() {
        var selector = this.dataset.cellSelector;
        if (!selector) return;
        var self = this;
        Array.prototype.slice.call(this.children).forEach(function(child) {
          if (child.matches(selector) || child.querySelector(selector)) return;
          child.classList.add('ap-dr-carousel__leader');
          self.parentNode.insertBefore(child, self);
        });
      }

      _autoplayMs() {
        var ms = parseInt(this.dataset.autoplay, 10) || 0;
        if (!ms) return 0;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 0;
        if (this.dataset.autoplayMobileOnly === 'true') {
          var mobile = window.theme && theme.config && theme.config.bpSmall;
          if (!mobile) return 0;
        }
        return ms;
      }

      /* data-enable-desktop / data-enable-mobile scope the carousel to one
         breakpoint ("false" disables it there); missing attributes mean
         always on, so existing users are unaffected */
      _enabledHere() {
        var mobile = window.theme && theme.config && theme.config.bpSmall;
        var value = mobile ? this.dataset.enableMobile : this.dataset.enableDesktop;
        return value !== 'false';
      }

      _build() {
        if (!this._enabledHere()) {
          this.classList.add('ap-dr-carousel--off');
          this.classList.remove('ap-dr-carousel--static');
          return;
        }
        this.classList.remove('ap-dr-carousel--off');
        if (this._realCount() <= this._perRow()) {
          this.classList.add('ap-dr-carousel--static');
          return;
        }
        this.classList.remove('ap-dr-carousel--static');

        var autoplay = this._autoplayMs();
        if (autoplay > 0 && !this._userTookOver && !(window.Shopify && Shopify.designMode)) {
          this._buildMarquee(autoplay);
          return;
        }

        if (!(window.theme && theme.Slideshow)) return;
        this.slideshow = new theme.Slideshow(this, {
          prevNextButtons: this.dataset.arrows === 'true',
          pageDots: this.dataset.dots === 'true',
          groupCells: true,
          wrapAround: false,
          adaptiveHeight: false,
          contain: true,
          cellAlign: 'left',
          dragThreshold: 10
        });
      }

      _realCount() {
        return this.querySelectorAll(':scope > :not([data-ap-dr-clone])').length;
      }

      /* Fluid auto-movement: cells are cloned once for a seamless loop and
         the strip animates continuously via CSS. Hover pauses; the first
         touch/click hands over to the normal draggable carousel. */
      _buildMarquee(intervalMs) {
        var self = this;

        if (!this._cloned) {
          Array.prototype.slice.call(this.children).forEach(function(cell) {
            var clone = cell.cloneNode(true);
            clone.setAttribute('data-ap-dr-clone', '');
            clone.setAttribute('aria-hidden', 'true');
            self.appendChild(clone);
          });
          this._cloned = true;
        }

        this.classList.add('ap-dr-carousel--marquee');

        requestAnimationFrame(function() {
          var shift = self.scrollWidth / 2;
          var cell = self.firstElementChild;
          var cellWidth = cell ? cell.offsetWidth : 300;
          /* interval = seconds one card takes to pass */
          var speed = cellWidth / (intervalMs / 1000);
          self.style.setProperty('--marquee-shift', shift + 'px');
          self.style.setProperty('--marquee-duration', (shift / speed) + 's');
        });

        if (!this._onTakeOver) {
          this._onTakeOver = function() {
            self._userTookOver = true;
            self._rebuild();
          };
          this.addEventListener('touchstart', this._onTakeOver, { passive: true, once: true });
          this.addEventListener('pointerdown', this._onTakeOver, { once: true });
        }
      }

      _teardownMarquee() {
        this.classList.remove('ap-dr-carousel--marquee');
        this.style.removeProperty('--marquee-shift');
        this.style.removeProperty('--marquee-duration');
        if (this._cloned) {
          this.querySelectorAll('[data-ap-dr-clone]').forEach(function(clone) {
            clone.remove();
          });
          this._cloned = false;
        }
        if (this._onTakeOver) {
          this.removeEventListener('touchstart', this._onTakeOver);
          this.removeEventListener('pointerdown', this._onTakeOver);
          this._onTakeOver = null;
        }
      }

      _rebuild() {
        this._teardown();
        this._build();
      }

      _teardown() {
        this._teardownMarquee();
        if (this.slideshow && typeof this.slideshow.destroy === 'function') {
          this.slideshow.destroy();
        }
        this.slideshow = null;
      }

      _handleBlockSelect(evt) {
        if (!this.slideshow || !this.contains(evt.target)) return;
        var cell = evt.target;
        while (cell && cell.parentElement !== this) {
          cell = cell.parentElement;
        }
        if (!cell) return;
        var index = Array.prototype.indexOf.call(this.children, cell);
        if (index > -1 && this.slideshow.slideshow) {
          this.slideshow.slideshow.selectCell(index);
        }
      }

      disconnectedCallback() {
        if (this._onReady) document.removeEventListener('DOMContentLoaded', this._onReady);
        if (this._onBreakpoint) {
          document.removeEventListener('matchSmall', this._onBreakpoint);
          document.removeEventListener('unmatchSmall', this._onBreakpoint);
        }
        if (this._onBlockSelect) document.removeEventListener('shopify:block:select', this._onBlockSelect);
        this._teardown();
      }
    }

    customElements.define('ap-dr-carousel', ApDrCarousel);
  }

  /* ---- <ap-dr-countdown> — chip countdown ----
     data-mode: 'fixed' (data-deadline = epoch ms) | 'evergreen'
     (data-minutes, per-visitor deadline in sessionStorage under data-key).
     data-expiry: 'hide' | 'zeros' | 'restart'. Chips carry data-unit
     (days/hours/minutes/seconds); JS only fills digits. */
  if (!customElements.get('ap-dr-countdown')) {
    class ApDrCountdown extends HTMLElement {
      connectedCallback() {
        this.chips = {};
        var self = this;
        this.querySelectorAll('[data-unit]').forEach(function(el) {
          self.chips[el.dataset.unit] = el;
        });
        this.deadline = this._resolveDeadline();
        this._tick = this._render.bind(this);
        this._render();
        this.interval = setInterval(this._tick, 1000);
      }

      _resolveDeadline() {
        if (this.dataset.mode === 'fixed') {
          var parsed = Date.parse(this.dataset.deadline);
          return isNaN(parsed) ? null : parsed;
        }
        var key = 'ap-dr-cd-' + (this.dataset.key || 'default');
        var stored = null;
        try { stored = sessionStorage.getItem(key); } catch (e) {}
        var now = Date.now();
        if (stored && parseInt(stored, 10) > now) return parseInt(stored, 10);
        var deadline = now + (parseInt(this.dataset.minutes, 10) || 240) * 60000;
        try { sessionStorage.setItem(key, String(deadline)); } catch (e) {}
        return deadline;
      }

      _render() {
        if (this.deadline === null) return;
        var diff = this.deadline - Date.now();

        if (diff <= 0) {
          switch (this.dataset.expiry) {
            case 'hide':
              this.classList.add('ap-dr-countdown--expired');
              clearInterval(this.interval);
              return;
            case 'restart':
              if (this.dataset.mode === 'evergreen') {
                try { sessionStorage.removeItem('ap-dr-cd-' + (this.dataset.key || 'default')); } catch (e) {}
                this.deadline = this._resolveDeadline();
                diff = this.deadline - Date.now();
                break;
              }
              diff = 0;
              break;
            default:
              diff = 0;
          }
        }

        var total = Math.max(0, Math.floor(diff / 1000));
        var values = {
          days: Math.floor(total / 86400),
          hours: Math.floor(total % 86400 / 3600),
          minutes: Math.floor(total % 3600 / 60),
          seconds: total % 60
        };
        if (!this.chips.days) {
          values.hours += values.days * 24;
        }
        for (var unit in this.chips) {
          var text = String(values[unit]);
          if (text.length < 2) text = '0' + text;
          if (this.chips[unit].textContent !== text) {
            this.chips[unit].textContent = text;
          }
        }
      }

      disconnectedCallback() {
        clearInterval(this.interval);
      }
    }

    customElements.define('ap-dr-countdown', ApDrCountdown);
  }
})();
